import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';
import { Box, BlockStack, Spinner, Text, Banner } from '@/lib/polaris';
import { EDGE_FUNCTIONS } from '@/config/functions.config';
import { storePath } from '@/config/routes.config';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { devLog } from '@/lib/utils';

/**
 * Componente para manejar el callback de autenticación de Supabase
 * Se ejecuta cuando el usuario vuelve desde un magic link o email de confirmación
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { storeSlug } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const redirectTimeoutRef = useRef<number | null>(null);

  const clearRedirectTimeout = useCallback(() => {
    if (redirectTimeoutRef.current !== null) {
      window.clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  }, []);

  const redirectToStore = useCallback(() => {
    clearRedirectTimeout();
    redirectTimeoutRef.current = window.setTimeout(() => {
      if (storeSlug) {
        navigate(storePath(storeSlug, ''), { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    }, 400);
  }, [clearRedirectTimeout, navigate, storeSlug]);

  const redirectToLogin = useCallback(() => {
    clearRedirectTimeout();
    redirectTimeoutRef.current = window.setTimeout(() => {
      navigate('/login', { replace: true });
    }, 1500);
  }, [clearRedirectTimeout, navigate]);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const hash = window.location.hash;
        const adminToken = searchParams.get('admin_token');
        const tenantId = searchParams.get('tenant_id');

        if (hash) {
          // Esperar a que Supabase procese el hash y establezca la sesión
          // Esto puede tomar un momento, así que esperamos hasta que la sesión esté disponible
          let attempts = 0;
          let session = null;
          
          while (attempts < 15) { // Esperar hasta 3 segundos (15 * 200ms)
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (currentSession?.user) {
              session = currentSession;
              break;
            }
            await new Promise(resolve => setTimeout(resolve, 200));
            attempts++;
          }

          if (!session?.user) {
            throw new Error('No se pudo establecer la sesión desde el magic_link');
          }

          if (adminToken) {
            try {
              const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
                EDGE_FUNCTIONS.VALIDATE_ADMIN_LOGIN_TOKEN,
                {
                  body: { token: adminToken },
                }
              );

              if (tokenError || !tokenData?.success) {
                devLog.warn('⚠️ [AuthCallback] Error validando admin_token, continuando sin él:', tokenError);
              } else if (tokenData.tenant_id) {
                localStorage.setItem('cached_tenant_id', tokenData.tenant_id);
              }
            } catch (error: unknown) {
              devLog.warn('⚠️ [AuthCallback] Error validando admin_token, continuando:', error);
            }
            
            // Limpiar admin_token de la URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('admin_token');
            if (tenantId) {
              newUrl.searchParams.delete('tenant_id');
            }
            newUrl.hash = ''; // Limpiar también el hash
            window.history.replaceState({}, '', newUrl.pathname + newUrl.search);
          } else {
            // Limpiar solo el hash
            window.history.replaceState({}, '', window.location.pathname + window.location.search);
          }

          setStatus('success');
          if (tenantId && !localStorage.getItem('cached_tenant_id')) {
            localStorage.setItem('cached_tenant_id', tenantId);
          }
          redirectToStore();
          return;
        }

        // PRIORIDAD 2: Si no hay hash pero hay admin_token, validarlo directamente
        // (método fallback cuando no se usa magic_link)
        if (adminToken) {
          try {
            const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
              EDGE_FUNCTIONS.VALIDATE_ADMIN_LOGIN_TOKEN,
              {
                body: { token: adminToken },
              }
            );

            if (tokenError || !tokenData?.success) {
              throw new Error(tokenError?.message || 'Token inválido');
            }

            // Obtener la sesión actual (debería estar establecida por Supabase)
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            
            if (sessionError || !session) {
              throw new Error('No se pudo obtener la sesión. Por favor, inicia sesión manualmente.');
            }

            // Establecer el tenant_id en el estado y caché
            if (tokenData.tenant_id) {
              localStorage.setItem('cached_tenant_id', tokenData.tenant_id);
            }

            // Limpiar el admin_token de la URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('admin_token');
            if (tenantId) {
              newUrl.searchParams.delete('tenant_id');
            }
            window.history.replaceState({}, '', newUrl.pathname + newUrl.search);

            setStatus('success');
            redirectToStore();
            return;
          } catch (error: unknown) {
            devLog.error('❌ [AuthCallback] Error validando admin_token:', error);
            setError((error as Error)?.message || 'Error al validar token de acceso');
            setStatus('error');
            redirectToLogin();
            return;
          }
        }

        setError('No se encontraron parámetros de autenticación');
        setStatus('error');
        redirectToLogin();
      } catch (err: unknown) {
        devLog.error('❌ [AuthCallback] Error en callback:', err);
        setError(getErrorMessage(err, 'Error al procesar autenticación'));
        setStatus('error');
        redirectToLogin();
      }
    };

    handleAuthCallback();
    return () => {
      clearRedirectTimeout();
    };
  }, [searchParams, redirectToStore, redirectToLogin, clearRedirectTimeout]);

  // Redirigir a /store/:storeSlug en cuanto tengamos storeSlug (puede llegar tras el fetch de perfil)
  useEffect(() => {
    if (status === 'success' && storeSlug) {
      clearRedirectTimeout();
      navigate(storePath(storeSlug, ''), { replace: true });
    }
  }, [status, storeSlug, navigate, clearRedirectTimeout]);

  if (status === 'loading') {
    return (
      <Box
        minHeight="100vh"
        padding="800"
        background="bg-fill"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BlockStack gap="400">
          <Box minHeight="60px" display="flex" alignItems="center" justifyContent="center">
            <Spinner size="large" />
          </Box>
          <Text as="p" variant="bodyMd" tone="subdued">
            Verificando autenticación...
          </Text>
        </BlockStack>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box
        minHeight="100vh"
        padding="800"
        background="bg-fill"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box maxWidth="400px">
          <Banner tone="critical" title="Error">
            <BlockStack gap="200">
              <Text as="p">{error || 'Error al procesar autenticación'}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Redirigiendo al login...
              </Text>
            </BlockStack>
          </Banner>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      padding="800"
      background="bg-fill"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box maxWidth="400px">
        <Banner tone="success" title="Autenticación exitosa">
          <Text as="p">Redirigiendo...</Text>
        </Banner>
      </Box>
    </Box>
  );
}
